/**
 * A link that looks like a button, for calls to action that navigate rather than act in place.
 * Separate from `Button` because an `<a>` and a `<button>` differ in what keyboard and screen
 * reader users expect, and styling one as the other is how a page ends up with links that need a
 * click handler. It draws with Button's own style objects, so the two cannot drift.
 *
 * `tone` is the name from before the variants: `solid` is `primary` and `quiet` is `secondary`.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import type { ButtonCorners, ButtonSize, ButtonVariant } from "./buttonStyle.ts";
import { buttonStyle } from "./buttonStyle.ts";
import { focusRing } from "./focus.ts";
import type { Navigate } from "./Link.tsx";
import { followWith } from "./Link.tsx";
export interface LinkButtonProps {
  readonly children: ReactNode;
  readonly href: string;
  readonly variant?: Exclude<ButtonVariant, "danger" | "dangerSecondary">;
  readonly size?: ButtonSize;
  readonly corners?: ButtonCorners;
  readonly leadingIcon?: ReactNode;
  readonly trailingIcon?: ReactNode;
  readonly fullWidth?: boolean | "phone";
  /** The app's router, for a link inside the app. */
  readonly navigate?: Navigate;
  readonly testId?: string;
  /** Before `variant`. */
  readonly tone?: "solid" | "quiet";
}
export function LinkButton({
  children,
  href,
  variant,
  size = "lg",
  corners = "pill",
  leadingIcon,
  trailingIcon,
  fullWidth = false,
  navigate,
  testId,
  tone
}: LinkButtonProps) {
  const look = variant ?? (tone === "quiet" ? "secondary" : "primary");
  return <a href={href} onClick={followWith(navigate, href)} data-testid={testId} {...stylex.props(buttonStyle.base, corners === "pill" ? buttonStyle.pill : buttonStyle.rounded, buttonStyle[size], fullWidth === true && buttonStyle.fullWidth, fullWidth === "phone" && buttonStyle.fullWidthPhone, buttonStyle[look], focusRing)}>
      {leadingIcon !== undefined && <span {...stylex.props(buttonStyle.slot)}>{leadingIcon}</span>}
      <span {...stylex.props(buttonStyle.label)}>{children}</span>
      {trailingIcon !== undefined && <span {...stylex.props(buttonStyle.slot)}>{trailingIcon}</span>}
    </a>;
}